import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import { columnsStaffUpdateRequests } from "../../Global Functions/columnsAntd";
import { ListStaffUpdateRequests, ApproveRejectStaffUpdateRequest } from "../../Redux/Actions/agentActions";
import ListTopSearch from "../../Global Functions/listTopSearch";
import DateRangePopup from "../../Utils/dateFilterPopup";
import toast from "react-hot-toast";

const ListStaffUpdateRequestsPage = () => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [rejectPopup, setRejectPopup] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);

  const { staffUpdateRequests, loading } = useSelector((state) => state.agents || {});

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const fetchDetails = useCallback(async (page, pageSize, showLoader = true) => {
    const payload = {
      page,
      limit: pageSize,
      is_deleted: false,
      filter: debouncedSearch,
      ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
      ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
    };
    try {
      await dispatch(ListStaffUpdateRequests(payload, showLoader));
      if (!showLoader) toast.success("List refreshed");
    } catch {
      if (showLoader) toast.error("Failed to fetch list");
    }
  }, [dispatch, debouncedSearch, dateRange]);

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, fetchDetails]);

  useEffect(() => {
    if (staffUpdateRequests?.total) {
      setPagination((prev) => ({ ...prev, total: staffUpdateRequests.total }));
    }
  }, [staffUpdateRequests]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleTableChange = (pagination) => setPagination(pagination);

  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleApproveReject = async (record, status) => {
    if (status === "rejected") {
      setSelectedRecord(record);
      setRejectPopup(true);
      return;
    }
    const res = await dispatch(ApproveRejectStaffUpdateRequest({
      id: record._id,
      status: "approved",
    }));
    if (res?.statusCode === 200) {
      fetchDetails(pagination.current, pagination.pageSize);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a reject reason");
      return;
    }
    try {
      setRejectLoading(true);
      const res = await dispatch(ApproveRejectStaffUpdateRequest({
        id: selectedRecord._id,
        status: "rejected",
        rejected_reason: rejectReason,
      }));
      if (res?.statusCode === 200) {
        setRejectPopup(false);
        setRejectReason("");
        setSelectedRecord(null);
        fetchDetails(pagination.current, pagination.pageSize);
      }
    } catch {
      toast.error("Failed to reject request");
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <>
      <div className="bookings_table">
        <div className="table_component_all">
          <div style={{ gridColumn: "span 5", height: "auto" }}>
            <ListTopSearch
              h3heading="Staff Update Requests"
              setState={setSearchQuery}
              onCalendarClick={() => setIsDatePopupVisible(true)}
            />
          </div>

          <div className="custom_table">
            <div style={{ padding: "0px 60px" }}>
              <Table
                dataSource={(Array.isArray(staffUpdateRequests?.data) && staffUpdateRequests.data) || []}
                columns={columnsStaffUpdateRequests(handleApproveReject)}
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 1700, y: 500 }}
                rowKey="_id"
                rowClassName={() => "text-center"}
              />
            </div>
          </div>
        </div>
      </div>

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />

      {rejectPopup && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(15,17,32,0.45)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 16,
            width: "min(480px, 100%)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
            overflow: "hidden",
          }}>
            <div style={{
              background: "#0D152B", padding: "18px 22px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <p style={{ color: "#fff", fontWeight: 600, fontSize: 15, margin: 0 }}>Reject Request</p>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, margin: 0 }}>
                  Agent: {selectedRecord?.staff?.name || "—"}
                </p>
              </div>
              <button
                onClick={() => { setRejectPopup(false); setRejectReason(""); setSelectedRecord(null); }}
                style={{
                  background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff", width: 28, height: 28, borderRadius: 6,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", fontSize: 12,
                }}
              >✕</button>
            </div>

            <div style={{ padding: "20px 22px" }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#5b6178", display: "block", marginBottom: 8 }}>
                Reason for rejection <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={4}
                disabled={rejectLoading}
                style={{
                  width: "100%", padding: "10px 14px",
                  border: "1.5px solid #e4e7f0", borderRadius: 10,
                  fontFamily: "inherit", fontSize: 14, color: "#0f1120",
                  background: "#f9fafb", resize: "vertical",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{
              display: "flex", justifyContent: "flex-end", gap: 8,
              padding: "12px 22px 18px", borderTop: "1px solid #e4e7f0",
            }}>
              <button
                onClick={() => { setRejectPopup(false); setRejectReason(""); setSelectedRecord(null); }}
                disabled={rejectLoading}
                style={{
                  padding: "10px 20px", borderRadius: 10,
                  background: "#f9fafb", border: "1.5px solid #e4e7f0",
                  color: "#5b6178", fontFamily: "inherit", fontSize: 13.5,
                  fontWeight: 500, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={rejectLoading}
                style={{
                  padding: "10px 20px", borderRadius: 10,
                  background: "#b91c1c", border: "none",
                  color: "#fff", fontFamily: "inherit", fontSize: 13.5,
                  fontWeight: 500, cursor: "pointer",
                  opacity: rejectLoading ? 0.5 : 1,
                }}
              >
                {rejectLoading ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListStaffUpdateRequestsPage;